/**
 * useSubscriptionPlans — React Query hook for the subscription tiers.
 *
 * Fetches GET /system-config/subscription-plans via the billing service and exposes
 * the plan list + shared statementDescriptor with loading/error/refetch state.
 * Modelled directly on useTopupPacks (frontend/src/hooks/useTopupPacks.ts).
 *
 * Query key: ['billing', 'subscription-plans']
 * staleTime: 5 minutes — matches the backend SystemConfig cache TTL; plans change
 *   rarely (config admin op, not a user action).
 *
 * Error handling: isError is exposed; the component renders ErrorState with
 *   onRetry=refetch. Errors are NEVER swallowed (KB TO-LL-036).
 *
 * CCM-03: consumed only by PlanAndCoinsTab, which mounts only under ParentGuard.
 * CCM-04: plans come from the API response; only static copy is in the frontend.
 * See Requirements/PRD-Subscription-Tiers.md §2.2 / §7.
 */

import { useQuery } from '@tanstack/react-query';
import { getSubscriptionPlans } from '@/services/billing';
import type { SubscriptionPlan, SubscriptionPlansResponse } from '@/types/billing';

export interface UseSubscriptionPlansReturn {
  /** The config-driven list of subscription tiers (empty array during loading) */
  plans: SubscriptionPlan[];
  /** Shared statement descriptor shown near the cards (empty string during loading) */
  statementDescriptor: string;
  /** True while the initial fetch is in-flight */
  isLoading: boolean;
  /** True when the fetch failed (render ErrorState with onRetry=refetch) */
  isError: boolean;
  /** Error object — undefined when no error */
  error: unknown;
  /** Manually trigger a fresh fetch (used by ErrorState retry button) */
  refetch: () => void;
}

export function useSubscriptionPlans(): UseSubscriptionPlansReturn {
  const { data, isLoading, isError, error, refetch } = useQuery<SubscriptionPlansResponse, unknown>({
    queryKey: ['billing', 'subscription-plans'],
    queryFn: () => getSubscriptionPlans(),
    staleTime: 5 * 60 * 1000, // 5 minutes — matches backend SystemConfigService cache TTL
  });

  return {
    plans: data?.plans ?? [],
    statementDescriptor: data?.statementDescriptor ?? '',
    isLoading,
    isError,
    error,
    refetch: () => {
      refetch();
    },
  };
}
